import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { COMPANY_ADDRESS, EMPTY, REGISTRATION_TYPE } from "./constants/config";
import Company from "./models/Company";
import store from "./store";


const CURRENCY_SYMBOL = '$';

export const shareMask = createNumberMask({
    prefix: '',
    allowDecimal: false,
    includeThousandsSeparator: true,
    allowNegative: false,
});

export const amountMask = createNumberMask({
    prefix: '',
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: 2,
    includeThousandsSeparator: true,
    allowNegative: false,
});

export const floatMask = decimalPoint => createNumberMask({
    prefix: '',
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: decimalPoint,
    includeThousandsSeparator: true,
    allowNegative: false,
});

export const unMask = (data, maskKeys = undefined) => {
    if(typeof data === 'object' && maskKeys) {
        Object.keys(data).forEach(key => {
            if(maskKeys.includes(key) && typeof data[key] === "string") {
                // eslint-disable-next-line no-param-reassign
                data[key] = Number(data[key].replace(/[$,]/g, ''));
            }
        });
        return data;
    }
    if(typeof data === 'string' && data === '0')
    {
        return 0;
    }
    if(typeof data === 'string')
        return Number(data.replace(/[$,]/g, ''));

    return data;
};

export const getAvatarInitial = fullName => {
    const nameArray = fullName.split(" ");
    return `${nameArray[0][0]}${nameArray[nameArray.length - 1][0]}`;
};

export const isAdmin = (passedUser = null) => {
    if (passedUser) return passedUser.is_secretary_admin;

    const user = store.getters['auth/user'];
    return user && user.is_secretary_admin;
};

export const isNotAdmin = (passedUser = null) => ! isAdmin(passedUser);

export const isExists = (data, key = null) => {
    if (key) return data[key];

    return !!data;
};

// alias to the isAdmin method
export const userIsAdmin = (passedUser = null) => isAdmin(passedUser);

/*
|--------------------------------------------------------------------------
| < Formatters / Converter >
|--------------------------------------------------------------------------
*/
// this will capitalize only the first letter of the whole string
export const capitalize = string => string.charAt(0).toUpperCase() + string.toLowerCase().slice(1);

export const capitalizeEveryWordFirstLetter = string => string.replace(/\b\w/g, char => char.toUpperCase());

export const getCurrencySymbol = () => CURRENCY_SYMBOL;

// 30232 => 323.22
export const toFixed = (number, fractionDigits = 2) => parseFloat(number).toFixed(fractionDigits);

// 3232 => 3,232.00
export const formatFloat = (number, fractionDigits = 2) => number && parseFloat(number).toFixed(fractionDigits).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// 3232 => 3,232
export const formatNumber = number => number && parseInt(number, 10).toLocaleString();

export const formatPrice = (price, symbol = null) => `${symbol || getCurrencySymbol()} ${formatFloat(price, )}`;

export const readableInteger = number => number && parseInt(number.replaceAll(',', ""), 10).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const readableDecimal = number => number && parseFloat(number.toString().replaceAll(",", "")).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const formatDecimal = number => {
    const decimalPlaces = (parseFloat(number).toString().split('.')[1] || []).length;
    const fractionDigits = decimalPlaces > 2 ? decimalPlaces : 2;

    return formatFloat(number, fractionDigits);
};

export const formatDate = date => {
    if (date)
    {
        const dateObj = new Date(date);
        const month = dateObj.toLocaleString("default", { month: "short" });
        const day = dateObj.getDate();
        const year = dateObj.getFullYear();
        
        const result = `${day} ${month} ${year}`;
        return result;
    }

    return EMPTY;
};

export const formatDateWithTime = date => {
    if (date)
    {
        const dateObj = new Date(date);
        const month = dateObj.toLocaleString("default", { month: "short" });
        const day = dateObj.getDate();
        const year = dateObj.getFullYear();

        const hours = dateObj.getHours();
        const minutes = dateObj.getMinutes();
        // const seconds = dateObj.getSeconds();
        
        const result = `${day} ${month} ${year} ${hours}:${minutes}`;
        return result;
    }

    return EMPTY;
};


export const mimeToFileName = mime => mime.split('/').pop();

export const timestampToDate = timestamp => {
    if(timestamp)
    {
        const date = new Date(timestamp * 1000);

        return formatDate(date);
    }
    return '-';
};

/*
|--------------------------------------------------------------------------
| </ Formatters / Converter >
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| < cool >
| 
| list of cool functions to do fancy things like lodash js library
|--------------------------------------------------------------------------
*/

/**
 * This function will try to get values from nested object 
 * 
 * @param {string} key 
 * @param {object} object 
 * @returns 
 */
export const pluck = (key, object) => {
    // if key includes . which means it is a nested object
    if (key.includes('.')) {
        // so fetch the left part of the key
        const nestedKey = key.split(".")[0];
        // and loop again the nested object finish
        return pluck(key.replace(`${nestedKey}.`, ''), object[nestedKey]);
    } 

    return object[key];
};

export const limitText = (str, wordLimit = 100) => {
    if (str.length > wordLimit) {
        return `${str.substring(0, wordLimit)}...`;
    }

    return str;
};

export const generateRandomUniqueId = () => Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

export const debounce = (func, timeout = 300) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => { func.apply(this, args); }, timeout);
    };
};

export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export const isPast = date => {
    const now = new Date();
    const past = new Date(date);

    return past < now;
};

export const iCompareString = (value1, value2) => value1.toLowerCase() === value2.toLowerCase();

/*
|--------------------------------------------------------------------------
| < / cool >
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| < important >
|--------------------------------------------------------------------------
*/

export const updateCurrentCompanyInStore = async () => {
    store.commit('company/UPDATE_CURRENT_COMPANY', null);
    const company = await Company.getCurrentCompany();
    store.commit('company/UPDATE_CURRENT_COMPANY', company);

    // this is use to dynamic vertical nav menu and only update after update current company in store
    store.commit('company/SET_ACTIVE_COMPANY_STATUS', company.status);

    return company;
};

export const switchCompany = async companyId => {
    const company = await Company.switchCompany(companyId);

    return company;
};

export const getIntendedUrl = () => {
    const intendedRouteName = store.getters['auth/intendedRouteName'];
    if (intendedRouteName)
    {
        store.commit('auth/SET_INTENDED_ROUTE_NAME', null);
        localStorage.removeItem('redirect-to');
        
        let params = {};
        if (intendedRouteName === 'sign.document') {
            params = store.getters['document/documentsIntendedRouteParam'];
        }
        return { name: intendedRouteName, params };
    }

    return false;
};

export const disableUserFromDoingActionWithoutAccount = () => {
    this.$cache.remove('redirect-to-password-reset');
    this.$cache.remove('reset-token');
    this.$cache.remove('email');
};

/*
|--------------------------------------------------------------------------
| </ important >
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| < validator >
|--------------------------------------------------------------------------
*/

export const isValidEmail = email => /\S+@\S+\.\S+/.test(String(email).toLowerCase());

/*
|--------------------------------------------------------------------------
| < / validator >
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| < calculation >
|--------------------------------------------------------------------------
*/

export const calculateFileSize = bytes => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (!bytes || bytes === 0) return '0 Byte';
    // eslint-disable-next-line radix
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${Math.round(bytes / (1024 ** i), 2) } ${ sizes[i]}`;
};

/*
|--------------------------------------------------------------------------
| < / calculation >
|--------------------------------------------------------------------------
*/
const prepareStartupPackageSuggestion = () => {
    // user has an own address
    const registeredAddress = store.getters['company/registeredAddress'];
    registeredAddress.type = COMPANY_ADDRESS.OWN_ADDRESS;

    store.commit('company/UPDATE_REGISTERED_ADDRESS', registeredAddress);
};

const prepareStartupPlusPackageSuggestion = () => {
    // user does not have an own address
    const registeredAddress = store.getters['company/registeredAddress'];
    registeredAddress.type = COMPANY_ADDRESS.ENSTON_ADDRESS;
    registeredAddress.fee.name = COMPANY_ADDRESS.GOLD_PLAN;

    store.commit('company/UPDATE_REGISTERED_ADDRESS', registeredAddress);
};

const prepareStartupSuitePackageSuggestion = () => {
    // user does not have an own address
    const registeredAddress = store.getters['company/registeredAddress'];
    registeredAddress.type = COMPANY_ADDRESS.ENSTON_ADDRESS;
    registeredAddress.fee.name = COMPANY_ADDRESS.PLATINUM_PLAN;

    store.commit('company/UPDATE_REGISTERED_ADDRESS', registeredAddress);
};

const prepareOnlineStartupPackageSuggestion = () => {
    // address
    const registeredAddress = store.getters['company/registeredAddress'];
    registeredAddress.type = COMPANY_ADDRESS.ENSTON_ADDRESS;
    registeredAddress.fee.name = COMPANY_ADDRESS.PLATINUM_PLAN;

    store.commit('company/UPDATE_REGISTERED_ADDRESS', registeredAddress);

    // directors
};

const prepareRelocationStartupPackageSuggestion = () => {
    // address
    const registeredAddress = store.getters['company/registeredAddress'];
    registeredAddress.type = COMPANY_ADDRESS.ENSTON_ADDRESS;
    registeredAddress.fee.name = COMPANY_ADDRESS.PLATINUM_PLAN;

    store.commit('company/UPDATE_REGISTERED_ADDRESS', registeredAddress);

    // directors
};

export const preparePackageSuggestionBasedOnType = async () => {
    const type = store.getters['company/registrationType'];

    if (type === REGISTRATION_TYPE.START_UP) prepareStartupPackageSuggestion();
    else if(type === REGISTRATION_TYPE.STARTUP_PLUS) prepareStartupPlusPackageSuggestion();
    if (type === REGISTRATION_TYPE.START_SUITE) prepareStartupSuitePackageSuggestion();
    else if(type === REGISTRATION_TYPE.ONLINE_STARTUP) prepareOnlineStartupPackageSuggestion();
    else if(type === REGISTRATION_TYPE.RELOCATION_STARTUP) prepareRelocationStartupPackageSuggestion();
};

export const camelize = function(str) {
    return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => index === 0 ? word.toLowerCase() : word.toUpperCase()).replace(/\s+/g, '');
};

export const getIdType = id => {
    const identityNumber = String(id).trim();

    if (/^[ST]\d{7}[A-Z]$/.test(identityNumber)) {
        return "NRIC";
    } 

    if (/^[FG]\d{7}[A-Z]$/.test(identityNumber)) {
        return "FIN";
    }
    
    return "Passport";
};